import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { TooltipProvider } from '@monksflow/monks-ui';
import { useArtifactAssetUrl } from '@/entities/artifact';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { ImagePreviewV2 } from '@/shared/ui/ImageTag/ImagePreview';

interface Props {
  /** Artifact id to resolve a signed image URL from when no direct URL is known. */
  artifactId?: string;
  /** A directly-known URL: an attached file's URL, or the picker's signed thumbnail. */
  directUrl?: string;
  filename: string;
  /** Square edge length in px. */
  sizePx: number;
  /** Shown while the URL is unresolved (a kind/status icon). */
  fallback: ReactNode;
  /** Top-right control rendered over the thumbnail (remove, re-attach). */
  trailing: ReactNode;
}

/**
 * A square image thumbnail chip for an attachment. A directly-known URL (an
 * attached file, or the picker's signed thumbnail) shows immediately; otherwise
 * it resolves a fresh signed URL from the artifact id — so an image library
 * artifact renders as a thumbnail in the composer and in chat history, just like
 * an attached image file. Opens the full image on click.
 */
export function AttachmentImageChip(props: Props) {
  const { t } = useTranslation('chats');
  const workspaceId = useCurrentWorkspaceId();
  const url = useArtifactAssetUrl(workspaceId, props.artifactId, 'thumbnail', props.directUrl);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  return (
    // Self-provide a TooltipProvider so a `trailing` control that renders a
    // tooltip (the re-attach / remove buttons in chat history) resolves, since
    // this chip can mount outside any ancestor provider — same as AttachmentChip.
    <TooltipProvider>
      <div className="group relative inline-flex" data-testid="image-tag">
        <button
          type="button"
          aria-label={t('chats.attachmentImageChip.preview', { filename: props.filename })}
          onClick={() => url && setIsPreviewOpen(true)}
          style={{ width: props.sizePx, height: props.sizePx }}
          className="flex items-center justify-center overflow-hidden rounded-md bg-surface transition-opacity hover:opacity-90"
        >
          {url ? (
            <img src={url} alt={props.filename} className="h-full w-full object-cover" />
          ) : (
            props.fallback
          )}
        </button>
        {props.trailing}
        <ImagePreviewV2
          open={isPreviewOpen}
          onClose={() => setIsPreviewOpen(false)}
          filename={props.filename}
          url={url}
        />
      </div>
    </TooltipProvider>
  );
}
